"use client";

import * as React from "react";
import {
  Plus,
  MoreVertical,
  Trash,
  ArrowRight,
  Sparkles,
  FileText,
  Calendar,
  ClipboardCheck,
  Link,
  ChevronsRight,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Notebook } from "../../../client";
import { CanvasCourse } from "@/lib/types";

/* shadcn */
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FinalAssignment, NotebookExtended } from "../../../types";
import { CourseAssignmentInline } from "./course-assignment-inline";

type StudySetButtonVariant = "create" | "existing";

interface BaseProps {
  variant: StudySetButtonVariant;
  onClick: () => void;
  className?: string;
}

/* ---------- Create variant ---------- */
interface CreateStudySetButtonProps extends BaseProps {
  variant: "create";
}

/* ---------- Existing variant ---------- */
interface ExistingStudySetButtonProps extends BaseProps {
  variant: "existing";
  notebook: NotebookExtended;
  course?: CanvasCourse;
  onDelete?: (notebook: Notebook) => void;
}

type StudySetButtonProps =
  | CreateStudySetButtonProps
  | ExistingStudySetButtonProps;

export function StudySetButton(props: StudySetButtonProps) {
  const { variant, onClick, className } = props;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  /* ---------- CREATE ---------- */
  if (variant === "create") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "rounded-lg border-2 border-dashed border-theme p-6",
          "bg-theme-0 hover:scale-110 transition",
          "flex items-center justify-center h-30",
          className
        )}
      >
        <div className="text-center space-y-2">
          <div className="mx-auto h-8 w-8 rounded-lg bg-theme-2 flex items-center justify-center">
            <Plus className="h-4 w-4 text-theme-0" />
          </div>
          <p className="text-base font-bold text-theme-0">Create Study Set</p>
        </div>
      </button>
    );
  }

  /* ---------- EXISTING ---------- */
  const { notebook, course, onDelete } = props;

  const assignmentName = notebook.assignment_id
    ? (notebook.metadata.assignment as FinalAssignment).name
    : null;

  return (
    <>
      <div
        onClick={onClick}
        className={cn(
          "relative text-left rounded-lg border-2 border-theme bg-card p-3 cursor-pointer bg-theme-0",
          "flex hover:shadow-theme transition h-30 group",
          className
        )}
      >
        {/* Action menu */}
        <div
          className="absolute top-2 right-2 hover:bg-theme-1 rounded-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-4 w-4 text-theme-0" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onClick} className="hover:bg-theme-1">
                <ArrowRight className="h-4 w-4 mr-2" />
                Open
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-red-700 hover:bg-theme-1"
                onClick={() => {
                  setMenuOpen(false); // critical
                  setConfirmDeleteOpen(true); // open dialog after
                }}
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Card content */}
        <div className="flex flex-col min-w-0 h-full">
          {/* Course — always at top */}
          <CourseAssignmentInline
            className="mt-2"
            course={course}
            assignmentName={assignmentName}
            color
            colorSize="medium"
          />

          {/* Centered content */}

          <div className="flex-1 flex flex-col justify-center gap-2">
            {/* Title */}
            <h3
              className={cn(
                "font-semibold text-base leading-tight",
                "line-clamp-2",
                "text-theme-0 group-hover:underline"
              )}
              title={notebook.name}
            >
              {notebook.name}
            </h3>

            {/* Generated materials (PRIMARY) */}
            <div className="flex items-center gap-2 text-xs font-semibold">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-2 py-0.5 text-blue-500">
                <Sparkles className="h-3.5 w-3.5" />
                {notebook.highlights.length} material
                {notebook.highlights.length !== 1 && "s"}
              </span>

              {/* Inputs + metadata (SECONDARY) */}
              <div className="flex items-center gap-3 text-xs text-theme-2">
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  {notebook.notebook_documents.length} source
                  {notebook.notebook_documents.length !== 1 && "s"}
                </span>

                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {notebook.created_at &&
                    new Date(notebook.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent className="bg-theme-0 text-theme-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete study set?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-semibold">{notebook.name}</span>. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel className="hover:bg-theme-1">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-400 text-black hover:bg-red-400"
              onClick={() => {
                onDelete?.(notebook);
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
